import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Lock, Pencil, Plus, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useStaff } from "@/context/StaffAuth";
import { api, errMsg } from "@/lib/api";
import { ErrorState, Field, PageHeader, StatusBadge } from "@/components/admin/AdminUI";

export default function AdminRoles() {
  const { can } = useStaff();
  const manage = can("roles.manage");
  const [roles, setRoles] = useState(null);
  const [perms, setPerms] = useState([]);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", permissions: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r, p] = await Promise.all([api.get("/admin/roles"), api.get("/admin/permissions")]);
      setRoles(r.data.items || []);
      setPerms(p.data.items || []);
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = "Roles & permissions · Admin"; }, []);

  const groups = useMemo(() => {
    const g = {};
    perms.forEach((p) => { (g[p.group] = g[p.group] || []).push(p); });
    return g;
  }, [perms]);

  const open = (role) => {
    setForm(role ? { name: role.name, description: role.description || "", permissions: role.permissions || [] } : { name: "", description: "", permissions: [] });
    setEditing(role || {});
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing?.id) await api.put(`/admin/roles/${editing.id}`, form);
      else await api.post("/admin/roles", form);
      toast.success(editing?.id ? "Role updated" : "Role created");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleGroup = (items, on) => {
    const keys = items.map((p) => p.key);
    setForm({ ...form, permissions: on ? Array.from(new Set([...form.permissions, ...keys])) : form.permissions.filter((k) => !keys.includes(k)) });
  };

  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div data-testid="admin-roles-page">
      <PageHeader eyebrow="Control" title="Roles & permissions" description="Roles bundle fine-grained permissions. System roles can be edited (except Administrator, which always holds everything); custom roles can be added for specific teams."
        actions={manage && <Button onClick={() => open(null)} className="rounded-lg" data-testid="admin-roles-new-button"><Plus className="h-4 w-4 mr-2" />New role</Button>} />
      {roles === null ? <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44 rounded-xl" />)}</div> : (
        <ul className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-roles-grid">
          {roles.map((r) => (
            <li key={r.id} className="bg-card border rounded-xl p-5 flex flex-col shadow-[0_1px_0_hsl(var(--shadow-1))]" data-testid="admin-role-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <span className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center">{r.key === "admin" ? <Lock className="h-4 w-4" /> : <Shield className="h-4 w-4" />}</span>
                  <div><h3 className="font-semibold text-sm">{r.name}</h3><p className="text-[11px] text-muted-foreground font-mono">{r.key}</p></div>
                </div>
                {r.is_system && <StatusBadge status="system" label="System" tone="muted" />}
              </div>
              <p className="text-sm text-muted-foreground mt-3 flex-1">{r.description}</p>
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-xs text-muted-foreground tabular">{r.key === "admin" ? "All" : r.permissions?.length || 0} permissions · {r.user_count || 0} user{r.user_count === 1 ? "" : "s"}</p>
                {manage && r.key !== "admin" && <Button variant="ghost" size="sm" onClick={() => open(r)} data-testid="admin-role-edit-button"><Pencil className="h-3.5 w-3.5 mr-1.5" />Edit</Button>}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-role-dialog">
          <DialogHeader><DialogTitle className="font-display text-3xl font-medium">{editing?.id ? `Edit ${editing.name}` : "New role"}</DialogTitle><DialogDescription>Tick the permissions this role grants. Changes apply to every assigned user immediately and are audited.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-5 pt-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Role name" htmlFor="role-name" required><Input id="role-name" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-role-name-input" /></Field>
              <Field label="Description" htmlFor="role-desc"><Textarea id="role-desc" rows={1} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-role-description-input" /></Field>
            </div>
            <div className="space-y-4">
              {Object.entries(groups).map(([g, items]) => {
                const all = items.every((p) => form.permissions.includes(p.key));
                return (
                  <fieldset key={g} className="border rounded-xl p-4" data-testid={`admin-role-group-${g.toLowerCase()}`}>
                    <legend className="px-2 text-xs uppercase tracking-[0.14em] text-muted-foreground font-medium flex items-center gap-3">{g}<button type="button" onClick={() => toggleGroup(items, !all)} className="normal-case tracking-normal text-[11px] underline underline-offset-4 hover:text-foreground">{all ? "Clear" : "Select all"}</button></legend>
                    <ul className="grid sm:grid-cols-2 gap-2.5">
                      {items.map((p) => (
                        <li key={p.key} className="flex items-start gap-3">
                          <Checkbox id={`perm-${p.key}`} checked={form.permissions.includes(p.key)} onCheckedChange={(v) => setForm({ ...form, permissions: v ? [...form.permissions, p.key] : form.permissions.filter((k) => k !== p.key) })} data-testid={`admin-role-perm-${p.key}`} />
                          <label htmlFor={`perm-${p.key}`} className="cursor-pointer"><span className="block text-sm font-mono leading-none">{p.key}</span><span className="block text-xs text-muted-foreground mt-1">{p.description}</span></label>
                        </li>
                      ))}
                    </ul>
                  </fieldset>
                );
              })}
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button type="submit" disabled={busy} data-testid="admin-role-save-button">{busy ? "Saving…" : editing?.id ? "Save role" : "Create role"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
